import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

/**
 * Loading screen — shown on page load.
 * Violet + lime theme with IRDO branding.
 */
const LoadingScreen = ({ onComplete }) => {
  const screenRef = useRef(null);
  const textRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        // Fade out loading screen
        gsap.to(screenRef.current, {
          opacity: 0,
          duration: 0.5,
          ease: 'power2.out',
          onComplete: () => {
            if (onComplete) onComplete();
          },
        });
      },
    });

    // Animate text
    tl.fromTo(
      textRef.current,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }
    )
      // Animate progress bar
      .fromTo(
        barRef.current,
        { scaleX: 0 },
        { scaleX: 1, duration: 1.2, ease: 'power2.inOut' },
        '-=0.3'
      )
      // Hold briefly
      .to({}, { duration: 0.3 });

    return () => {
      tl.kill();
    };
  }, [onComplete]);

  return (
    <div
      ref={screenRef}
      className="fixed inset-0 z-[10000] bg-background dark:bg-dark-bg flex flex-col items-center justify-center"
    >
      {/* Logo */}
      <div className="mb-8">
        <img src="/irdo-dark.png" alt="IRDO" className="h-12 w-auto" />
      </div>

      {/* Animated text */}
      <div ref={textRef} className="text-center mb-8">
        <span className="font-mono text-sm uppercase tracking-[0.2em] text-accent dark:text-dark-accent">
          Loading Experience
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-48 h-0.5 bg-border dark:bg-dark-border rounded-full overflow-hidden">
        <div
          ref={barRef}
          className="h-full bg-accent dark:bg-dark-accent origin-left"
          style={{ transform: 'scaleX(0)' }}
        />
      </div>

      {/* Decorative dots */}
      <div className="flex gap-2 mt-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-accent dark:bg-dark-accent animate-pulse"
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}
      </div>
    </div>
  );
};

export default LoadingScreen;